import Axios from 'axios';
import getConfig from 'lib/next-config-compat';

const { publicRuntimeConfig } = getConfig();

/**
 * Exchanges the refresh-token cookie for a new access token.
 *
 * The API rotates that cookie: each call consumes the current token and sets
 * the next one. Two calls in flight together therefore send the same value, and
 * whichever the API sees second is rejected — the "old refresh-token" failure
 * behind OSPACE3-353. It is easy to end up with two: _app refreshes on every
 * page load, the response interceptor in useApiRequest refreshes on any 401,
 * and pendingPaymentBlock refreshes again once a card has been registered, all
 * without knowing about each other.
 *
 * So there is one request at a time, module-scoped rather than per-component
 * because the callers do not share a component tree. Everyone who asks while
 * one is in flight waits on that one and gets the same token.
 */
let inFlight: Promise<string> | null = null;

export const refreshSession = (): Promise<string> => {
  if (!inFlight) {
    const client = Axios.create({
      baseURL: `${publicRuntimeConfig.baseUrl}/api`,
      withCredentials: true,
      headers: {
        Accept: `application/json`,
      },
    });

    inFlight = client
      .post('/v1/users/auth/refresh_token')
      .then(({ data }) => data.token as string)
      .finally(() => {
        // Cleared either way: a failure must not wedge every later refresh on a
        // rejected promise.
        inFlight = null;
      });
  }

  return inFlight;
};

export default refreshSession;
